import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const toc = [
  { id: 'collect', label: '1. Information we collect' },
  { id: 'use', label: '2. How we use it' },
  { id: 'legal-basis', label: '3. Legal basis' },
  { id: 'share', label: '4. How we share' },
  { id: 'location', label: '5. Location & Nearby' },
  { id: 'cookies', label: '6. Cookies' },
  { id: 'retention', label: '7. Data retention' },
  { id: 'security', label: '8. Security' },
  { id: 'rights', label: '9. Your rights' },
  { id: 'children', label: '10. Children' },
  { id: 'transfers', label: '11. Transfers' },
  { id: 'changes', label: '12. Policy changes' },
  { id: 'contact', label: '13. Contact us' },
];

const PrivacyPolicyPage = () => (
  <LegalPageLayout
    title="Privacy Policy"
    lastUpdated="July 11, 2026"
    lead="How UBSAAPP collects, uses, and protects your personal information — written clearly so you can book with confidence."
    toc={toc}
  >
    <div className="legal-page__intro">
      <p>
        This Privacy Policy explains how <strong>UBSAAPP</strong> (&quot;we&quot;, &quot;us&quot;), operated by{' '}
        <strong>AtoZ Auto</strong>, collects, uses, stores, and protects your personal information when
        you use our website, mobile app, and related services.
      </p>
      <p>
        By using UBSAAPP, you agree to the practices described here. If you do not agree, please
        discontinue use of our services.
      </p>
    </div>

    <section className="legal-page__section" id="collect">
      <h2>
        <span className="legal-page__section-num">01</span>
        Information We Collect
      </h2>
      <p>We may collect the following categories of information:</p>
      <ul>
        <li><strong>Account information:</strong> name, email address, phone number, profile photo, and login method (OTP, Google, etc.).</li>
        <li><strong>Booking information:</strong> stay dates, guest count, property preferences, meeting room details, payment status, and booking history.</li>
        <li><strong>Host &amp; listing data:</strong> property details, photos, pricing, identity or verification documents where required, and bank or payout details for hosts.</li>
        <li><strong>Communications:</strong> messages between guests and hosts, support enquiries, and feedback you submit.</li>
        <li><strong>Location data:</strong> city or area you search, GPS coordinates when you use Nearby search or grant location permission, and map interactions.</li>
        <li><strong>Device &amp; usage data:</strong> IP address, browser type, device identifiers, pages viewed, and cookies or similar technologies.</li>
        <li><strong>Payment data:</strong> transaction references processed by our payment partners (we do not store full card numbers on our servers).</li>
      </ul>
    </section>

    <section className="legal-page__section" id="use">
      <h2>
        <span className="legal-page__section-num">02</span>
        How We Use Your Information
      </h2>
      <p>We use personal information to:</p>
      <ul>
        <li>Create and manage your account and authenticate you (including sending OTP codes by email).</li>
        <li>Process bookings, payments, refunds, and customer support requests.</li>
        <li>Display relevant search results, recommendations, and nearby places.</li>
        <li>Enable messaging between guests and hosts.</li>
        <li>Send booking confirmations, reminders, security alerts, and service updates.</li>
        <li>Improve platform performance, security, and user experience.</li>
        <li>Comply with legal obligations and prevent fraud or abuse.</li>
      </ul>
    </section>

    <section className="legal-page__section" id="legal-basis">
      <h2>
        <span className="legal-page__section-num">03</span>
        Legal Basis (where applicable)
      </h2>
      <p>
        We process your data based on your consent (e.g. location permission, marketing where opted in),
        performance of a contract (bookings and account services), legitimate interests (security,
        analytics, product improvement), and legal obligations.
      </p>
    </section>

    <section className="legal-page__section" id="share">
      <h2>
        <span className="legal-page__section-num">04</span>
        How We Share Information
      </h2>
      <p>We may share information with:</p>
      <ul>
        <li><strong>Hosts &amp; guests</strong> — to fulfil bookings and enable communication.</li>
        <li><strong>Payment processors</strong> — to complete transactions securely.</li>
        <li><strong>Technology partners</strong> — such as email (SMTP), maps, search APIs, cloud hosting, and analytics providers who process data on our instructions.</li>
        <li><strong>Company workspaces</strong> — where you access meeting rooms or staff accounts linked to an organisation.</li>
        <li><strong>Authorities</strong> — when required by law, court order, or to protect rights and safety.</li>
      </ul>
      <p>We do not sell your personal information to third parties for their independent marketing.</p>
    </section>

    <section className="legal-page__section" id="location">
      <h2>
        <span className="legal-page__section-num">05</span>
        Location &amp; Nearby Search
      </h2>
      <p>
        When you tap <strong>Nearby</strong> or allow location access, we use your device GPS to show
        properties and places near you. You can deny permission and search manually instead. Location
        is used for search and map features and is not continuously tracked in the background unless
        a feature you enable explicitly requires it.
      </p>
    </section>

    <section className="legal-page__section" id="cookies">
      <h2>
        <span className="legal-page__section-num">06</span>
        Cookies &amp; Similar Technologies
      </h2>
      <p>
        We use cookies and local storage to keep you signed in, remember preferences, and understand
        how the platform is used. You can control cookies through your browser settings; some features
        may not work correctly if cookies are disabled.
      </p>
    </section>

    <section className="legal-page__section" id="retention">
      <h2>
        <span className="legal-page__section-num">07</span>
        Data Retention
      </h2>
      <p>
        We retain personal information for as long as your account is active and as needed to provide
        services, resolve disputes, enforce agreements, and meet legal record-keeping requirements.
        OTP codes expire within minutes and are not kept after verification.
      </p>
    </section>

    <section className="legal-page__section" id="security">
      <h2>
        <span className="legal-page__section-num">08</span>
        Security
      </h2>
      <p>
        We implement reasonable technical and organisational measures to protect your data, including
        encrypted connections (HTTPS), access controls, and secure authentication. No method of
        transmission over the internet is 100% secure; please use a strong password and protect your
        account credentials.
      </p>
    </section>

    <section className="legal-page__section" id="rights">
      <h2>
        <span className="legal-page__section-num">09</span>
        Your Rights &amp; Choices
      </h2>
      <p>Depending on applicable law, you may have the right to:</p>
      <ul>
        <li>Access, correct, or update your personal information in Account settings.</li>
        <li>Request deletion of your account and associated data, subject to legal retention needs.</li>
        <li>Withdraw consent for optional processing such as marketing emails.</li>
        <li>Object to or restrict certain processing where legally permitted.</li>
        <li>Lodge a complaint with a data protection authority.</li>
      </ul>
      <p>
        To exercise these rights, contact us at{' '}
        <a href="mailto:info.ubsaaap@atozasindia.in">info.ubsaaap@atozasindia.in</a>.
      </p>
    </section>

    <section className="legal-page__section" id="children">
      <h2>
        <span className="legal-page__section-num">10</span>
        Children&apos;s Privacy
      </h2>
      <p>
        UBSAAPP is not directed at children under 18. We do not knowingly collect personal information
        from minors. If you believe a child has provided us data, please contact us so we can delete it.
      </p>
    </section>

    <section className="legal-page__section" id="transfers">
      <h2>
        <span className="legal-page__section-num">11</span>
        International Transfers
      </h2>
      <p>
        Your data may be processed on servers located in India or other countries where our service
        providers operate. We take steps to ensure appropriate safeguards when data is transferred
        across borders.
      </p>
    </section>

    <section className="legal-page__section" id="changes">
      <h2>
        <span className="legal-page__section-num">12</span>
        Changes to This Policy
      </h2>
      <p>
        We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date at the top
        will reflect changes. Continued use after an update means you accept the revised policy.
      </p>
    </section>

    <section className="legal-page__section" id="contact">
      <h2>
        <span className="legal-page__section-num">13</span>
        Contact Us
      </h2>
      <div className="legal-page__contact">
        <p>
          For privacy questions or requests:<br />
          <strong>AtoZ Auto — UBSAAPP</strong><br />
          F-18, PH VI AYA NAGAR EXTN., New Delhi, Delhi, India – 110047<br />
          Email: <a href="mailto:info.ubsaaap@atozasindia.in">info.ubsaaap@atozasindia.in</a>
        </p>
      </div>
    </section>
  </LegalPageLayout>
);

export default PrivacyPolicyPage;
